import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Modal,
    InputBase,
    Chip,
    Menu,
    MenuItem
} from '@mui/material';
import { AddIcon, MoreVertIcon, SearchOutlinedIcon } from 'icons/materialIcons';
import TeamDarkButtonWithIcon from 'team/shared/components/button/TeamDarkButtonWithIcon';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import { getSubteam, updateSubteam } from 'team/apis/teamSubteamApi';
import { getTemplates } from 'team/apis/teamTemplateApi';

const TeamSubteamMembersTemplateContainer = ({ subteamId }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [subteamSettings, setSubteamSettings] = useState(null);
    const [originalSettings, setOriginalSettings] = useState(null);
    const [teamId, setTeamId] = useState(null);

    const [allTemplates, setAllTemplates] = useState([]);
    const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
    const [defaultTemplateId, setDefaultTemplateId] = useState(null);

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuTemplateId, setMenuTemplateId] = useState(null);

    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Fetch subteam details(including current settings)
    useEffect(() => {
        const fetchSubteam = async () => {
            if (!subteamId) return;

            setIsLoading(true);
            try {
                const response = await getSubteam(subteamId);
                const subteam = response?.subteam || response?.data?.subteam || response;

                const settings = subteam?.settings || {};
                setSubteamSettings(settings);
                setOriginalSettings(settings);

                // Existing auto-assign fields (backwards compatible)
                const existingSingle = settings.autoAssignTemplate
                    ? settings.autoAssignTemplate.toString()
                    : null;

                const autoAssignTemplates = Array.isArray(settings.autoAssignTemplates)
                    ? settings.autoAssignTemplates.map(id => id.toString())
                    : existingSingle
                        ? [existingSingle]
                        : [];

                const autoAssignDefault = settings.autoAssignDefaultTemplate
                    ? settings.autoAssignDefaultTemplate.toString()
                    : existingSingle
                        ? existingSingle
                        : autoAssignTemplates[0] || null;

                setSelectedTemplateIds(autoAssignTemplates);
                setDefaultTemplateId(autoAssignDefault);

                const teamIdValue = subteam.teamId?._id || subteam.teamId || null;
                setTeamId(teamIdValue ? teamIdValue.toString() : null);
            } catch (error) {
                console.error('Error fetching subteam for template auto-assign:', error);
                setNotification({
                    open: true,
                    message: error.message || 'Failed to load template auto-assign settings',
                    severity: 'error'
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchSubteam();
    }, [subteamId]);

    // Fetch all team templates once we know the teamId
    useEffect(() => {
        const fetchTemplates = async () => {
            if (!teamId) return;

            try {
                const response = await getTemplates(teamId);
                const templates =
                    response?.templates || response?.data?.templates || response?.data || [];
                setAllTemplates(Array.isArray(templates) ? templates : []);
            } catch (error) {
                console.error('Error fetching templates for subteam auto-assign:', error);
                setNotification({
                    open: true,
                    message: error.message || 'Failed to load templates',
                    severity: 'error'
                });
            }
        };

        fetchTemplates();
    }, [teamId]);

    // Build selected template objects from IDs
    const selectedTemplates = useMemo(() => {
        if (!Array.isArray(allTemplates) || allTemplates.length === 0) return [];
        return selectedTemplateIds
            .map(id =>
                allTemplates.find(
                    t => t._id === id || t._id?.toString() === id?.toString()
                )
            )
            .filter(Boolean);
    }, [allTemplates, selectedTemplateIds]);

    // Templates available to add (not already selected)
    const availableTemplates = useMemo(() => {
        if (!Array.isArray(allTemplates)) return [];
        const selectedSet = new Set(selectedTemplateIds.map(id => id.toString()));
        return allTemplates.filter(
            t => t?._id && !selectedSet.has(t._id.toString())
        );
    }, [allTemplates, selectedTemplateIds]);

    // Filtered available templates by search
    const filteredAvailableTemplates = useMemo(() => {
        if (!searchQuery.trim()) return availableTemplates;
        const query = searchQuery.toLowerCase();
        return availableTemplates.filter(template => {
            const name = (template.templateName || '').toLowerCase();
            return name.includes(query);
        });
    }, [availableTemplates, searchQuery]);

    // Determine if there are unsaved changes
    const hasChanges = useMemo(() => {
        if (!originalSettings) return false;

        const originalSingle = originalSettings.autoAssignTemplate
            ? originalSettings.autoAssignTemplate.toString()
            : null;

        const originalTemplates = Array.isArray(originalSettings.autoAssignTemplates)
            ? originalSettings.autoAssignTemplates.map(id => id.toString())
            : originalSingle
                ? [originalSingle]
                : [];

        const originalDefault = originalSettings.autoAssignDefaultTemplate
            ? originalSettings.autoAssignDefaultTemplate.toString()
            : originalSingle
                ? originalSingle
                : originalTemplates[0] || null;

        const currentTemplatesSorted = [...selectedTemplateIds].map(id => id.toString()).sort();
        const originalTemplatesSorted = [...originalTemplates].sort();

        const templatesChanged =
            currentTemplatesSorted.length !== originalTemplatesSorted.length ||
            currentTemplatesSorted.some((id, idx) => id !== originalTemplatesSorted[idx]);

        const defaultChanged =
            (defaultTemplateId || null)?.toString() !== (originalDefault || null)?.toString();

        return templatesChanged || defaultChanged;
    }, [originalSettings, selectedTemplateIds, defaultTemplateId]);

    const handleNotificationClose = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    const handleAddTemplateClick = () => {
        if (selectedTemplateIds.length >= 3 || availableTemplates.length === 0) {
            return;
        }
        setSearchQuery('');
        setAddModalOpen(true);
    };

    const handleAddTemplateSelect = (templateId) => {
        if (!templateId) return;
        const idStr = templateId.toString();
        if (selectedTemplateIds.includes(idStr) || selectedTemplateIds.length >= 3) {
            return;
        }
        setSelectedTemplateIds(prev => [...prev, idStr]);
        if (!defaultTemplateId) {
            setDefaultTemplateId(idStr);
        }
        setAddModalOpen(false);
    };

    const handleAddModalClose = () => {
        setAddModalOpen(false);
    };

    // Three-dots menu handlers
    const handleMenuOpen = (event, templateId) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setMenuTemplateId(templateId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuTemplateId(null);
    };

    const handleSetAsDefault = () => {
        if (!menuTemplateId) return;
        setDefaultTemplateId(menuTemplateId.toString());
        handleMenuClose();
    };

    const handleRemoveTemplate = () => {
        if (!menuTemplateId) return;
        const removeId = menuTemplateId.toString();
        setSelectedTemplateIds(prev => prev.filter(id => id.toString() !== removeId));

        // If the removed template was default, pick a new default (first in list) or clear
        if (defaultTemplateId && defaultTemplateId.toString() === removeId) {
            setDefaultTemplateId(prev => {
                const remaining = selectedTemplateIds.filter(id => id.toString() !== removeId);
                return remaining.length > 0 ? remaining[0].toString() : null;
            });
        }

        handleMenuClose();
    };

    const handleCancel = () => {
        if (!originalSettings) return;

        const originalSingle = originalSettings.autoAssignTemplate
            ? originalSettings.autoAssignTemplate.toString()
            : null;

        const originalTemplates = Array.isArray(originalSettings.autoAssignTemplates)
            ? originalSettings.autoAssignTemplates.map(id => id.toString())
            : originalSingle
                ? [originalSingle]
                : [];

        const originalDefault = originalSettings.autoAssignDefaultTemplate
            ? originalSettings.autoAssignDefaultTemplate.toString()
            : originalSingle
                ? originalSingle
                : originalTemplates[0] || null;

        setSelectedTemplateIds(originalTemplates);
        setDefaultTemplateId(originalDefault || null);
    };

    const handleSave = async () => {
        if (!subteamId || !subteamSettings) {
            setNotification({
                open: true,
                message: 'Subteam data is not loaded. Please refresh and try again.',
                severity: 'error'
            });
            return;
        }

        setIsSaving(true);
        try {
            const newSettings = {
                ...(originalSettings || {}),
                // For backwards compatibility, keep the single autoAssignTemplate aligned with default
                autoAssignTemplate: defaultTemplateId || null,
                autoAssignTemplates: selectedTemplateIds,
                autoAssignDefaultTemplate: defaultTemplateId || null
            };

            const response = await updateSubteam(subteamId, { settings: newSettings });
            const updatedSubteam = response?.subteam || response?.data?.subteam || null;
            const updatedSettings = updatedSubteam?.settings || newSettings;

            setSubteamSettings(updatedSettings);
            setOriginalSettings(updatedSettings);

            setNotification({
                open: true,
                message: 'Template auto-assign settings updated successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error saving template auto-assign settings:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to update template auto-assign settings',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box sx={{ padding: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Template Auto Assign
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 0.5, marginBottom: 2, color: '#666' }}>
                Select up to three templates to be auto assigned to members upon adding them to a subteam.
            </Typography>

            {/* Top row: Add Template button (left) and Cancel/Update (right) */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                }}
            >
                <TeamDarkButtonWithIcon
                    text="Add Template"
                    icon={<AddIcon />}
                    iconPosition="start"
                    onClick={handleAddTemplateClick}
                    disabled={isLoading || isSaving || selectedTemplateIds.length >= 3 || availableTemplates.length === 0}
                    sx={{
                        borderRadius: '20px',
                        paddingX: 2.5,
                        paddingY: 1,
                        fontSize: '0.75rem'
                    }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TeamWhiteButtonWithIcon
                        text="Cancel"
                        onClick={handleCancel}
                        disabled={isSaving || !hasChanges}
                        padding="10px 32px"
                        sx={{
                            minWidth: '120px',
                            '&.Mui-disabled': {
                                backgroundColor: '#f5f5f5',
                                color: '#c4c4c4',
                                borderColor: '#e3e3e3'
                            }
                        }}
                    />
                    <Box
                        component="button"
                        onClick={handleSave}
                        disabled={isSaving || !hasChanges}
                        style={{
                            border: 'none',
                            background: 'none',
                            padding: 0,
                            margin: 0
                        }}
                    >
                        <Box
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                padding: '10px 32px',
                                minWidth: '120px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isSaving || !hasChanges ? 'not-allowed' : 'pointer',
                                opacity: isSaving || !hasChanges ? 0.6 : 1
                            }}
                        >
                            {isSaving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Update'}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Selected templates list */}
            <Box sx={{ flex: 1, mt: 1 }}>
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 200
                        }}
                    >
                        <CircularProgress size={32} />
                        <Typography sx={{ ml: 2, color: '#666' }}>Loading templates...</Typography>
                    </Box>
                ) : selectedTemplates.length === 0 ? (
                    <Box
                        sx={{
                            border: '0.0625rem dashed #e0e0e0',
                            borderRadius: '0.75rem',
                            backgroundColor: '#fafafa',
                            padding: 3,
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                            No templates selected for this subteam.
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
                            Click &quot;Add Template&quot; to choose up to three templates from your team templates.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {selectedTemplates.map((template) => {
                            const id = template._id?.toString();
                            const isDefault =
                                defaultTemplateId &&
                                id &&
                                defaultTemplateId.toString() === id.toString();
                            return (
                                <Box
                                    key={id}
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '0.75rem',
                                        padding: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#333' }}
                                        >
                                            {template.templateName || 'Unnamed Template'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontSize: '0.75rem', color: '#828282' }}
                                        >
                                            template
                                        </Typography>
                                        {isDefault && (
                                            <Chip
                                                label="DEFAULT TEMPLATE"
                                                size="small"
                                                sx={{
                                                    mt: 0.5,
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    height: 20
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <IconButton
                                        onClick={(e) => handleMenuOpen(e, id)}
                                        sx={{
                                            color: '#828282'
                                        }}
                                    >
                                        <MoreVertIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {selectedTemplates.length > 0 && (
                    <Typography
                        variant="body2"
                        sx={{ mt: 1.5, color: '#999', fontSize: '0.75rem' }}
                    >
                        You can select up to three templates for this subteam. One of them is used as
                        the default when new members are added.
                    </Typography>
                )}
            </Box>

            {/* Add Template Modal */}
            <Modal open={addModalOpen} onClose={handleAddModalClose}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        maxWidth: 480,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                        maxHeight: '70vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Add Template
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                        Choose a template from your team templates to use for this subteam.
                    </Typography>

                    {/* Search bar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            backgroundColor: '#f7f7f7',
                            borderRadius: '20px',
                            border: '1px solid #e3e3e3',
                            padding: '8px 16px',
                            mb: 2,
                            '&:hover': {
                                boxShadow: 2
                            }
                        }}
                    >
                        <SearchOutlinedIcon
                            sx={{
                                color: '#828282',
                                fontSize: '18px',
                                marginRight: '8px'
                            }}
                        />
                        <InputBase
                            fullWidth
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search templates..."
                            sx={{
                                fontSize: '0.7rem',
                                color: '#4f4f4f',
                                '& .MuiInputBase-input::placeholder': {
                                    color: '#828282',
                                    opacity: 1
                                }
                            }}
                        />
                    </Box>

                    {/* Templates list */}
                    <Box sx={{ flex: 1, overflowY: 'auto', mt: 0.5 }}>
                        {filteredAvailableTemplates.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 160
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                    {availableTemplates.length === 0
                                        ? 'All templates are already selected for this subteam.'
                                        : 'No templates found matching your search.'}
                                </Typography>
                            </Box>
                        ) : (
                            filteredAvailableTemplates.map((template) => (
                                <Box
                                    key={template._id}
                                    onClick={() => handleAddTemplateSelect(template._id)}
                                    sx={{
                                        padding: 1.25,
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': { backgroundColor: '#f5f5f5' },
                                        mb: 0.5
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#333' }}
                                    >
                                        {template.templateName || 'Unnamed Template'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: '0.75rem', color: '#828282' }}
                                    >
                                        template
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Box>

                    {/* Modal footer buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            mt: 2
                        }}
                    >
                        <TeamWhiteButtonWithIcon
                            text="Cancel"
                            onClick={handleAddModalClose}
                            padding="8px 20px"
                        />
                    </Box>
                </Box>
            </Modal>

            {/* Per-template three-dots menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        minWidth: '200px'
                    }
                }}
            >
                <MenuItem onClick={handleSetAsDefault}>
                    Set as default
                </MenuItem>
                <MenuItem onClick={handleRemoveTemplate} sx={{ color: 'error.main' }}>
                    Remove template from this subteam
                </MenuItem>
            </Menu>

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                handleClose={handleNotificationClose}
            />
        </Box>
    );
};

export default TeamSubteamMembersTemplateContainer;


